import { useState, useEffect } from 'react'
import { X, Save, Loader2, Tag, Calendar } from 'lucide-react'
import { memoryApi, MemoryFact, MemoryFactCreate, MemoryFactUpdate, MemoryScope, MemoryCategory } from '../../lib/api'

interface FactModalProps {
  fact: MemoryFact | null
  scopes: MemoryScope[]
  categories: MemoryCategory[]
  onClose: () => void
  onSave: () => void
}

export default function FactModal({ fact, scopes, categories, onClose, onSave }: FactModalProps) {
  const [formData, setFormData] = useState({
    memory_scope_id: 0,
    category: '',
    fact_text: '',
    fact_summary: '',
    subcategory: '',
    source_type: 'manual',
    confidence_level: 'verified' as 'auto' | 'verified' | 'deprecated',
    tags: [] as string[],
    priority: 0,
    valid_until: '',
  })
  const [tagInput, setTagInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (fact) {
      setFormData({
        memory_scope_id: fact.memory_scope_id,
        category: fact.category,
        fact_text: fact.fact_text,
        fact_summary: fact.fact_summary || '',
        subcategory: fact.subcategory || '',
        source_type: fact.source_type,
        confidence_level: fact.confidence_level,
        tags: fact.tags || [],
        priority: fact.priority,
        valid_until: fact.valid_until ? fact.valid_until.split('T')[0] : '',
      })
    } else if (scopes.length > 0) {
      setFormData(prev => ({ ...prev, memory_scope_id: scopes[0].id }))
    }
  }, [fact, scopes])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (fact) {
        // Update
        const updateData: MemoryFactUpdate = {
          fact_text: formData.fact_text,
          fact_summary: formData.fact_summary || undefined,
          category: formData.category,
          subcategory: formData.subcategory || undefined,
          confidence_level: formData.confidence_level,
          tags: formData.tags,
          priority: formData.priority,
          valid_until: formData.valid_until || undefined,
        }
        await memoryApi.updateFact(fact.id, updateData)
      } else {
        // Create
        const createData: MemoryFactCreate = {
          memory_scope_id: formData.memory_scope_id,
          category: formData.category,
          fact_text: formData.fact_text,
          fact_summary: formData.fact_summary || undefined,
          subcategory: formData.subcategory || undefined,
          source_type: formData.source_type,
          confidence_level: formData.confidence_level,
          tags: formData.tags,
          priority: formData.priority,
          valid_until: formData.valid_until || undefined,
        }
        await memoryApi.createFact(createData)
      }
      onSave()
      onClose()
    } catch (err: any) {
      console.error('Failed to save fact:', err)
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setLoading(false)
    }
  }

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }))
      setTagInput('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))
  }

  const getCategoryColor = (slug: string) => {
    const cat = categories.find(c => c.slug === slug)
    return cat?.color || '#3B82F6'
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
            {fact ? 'Modifier le fait' : 'Nouveau fait mémoire'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Scope */}
          {!fact && (
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Scope <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.memory_scope_id}
                onChange={(e) => setFormData(prev => ({ ...prev, memory_scope_id: Number(e.target.value) }))}
                required
                className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {scopes.map(scope => (
                  <option key={scope.id} value={scope.id}>
                    {scope.name} ({scope.scope_type === 'tenant' ? 'Business' : 'End-User'})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-zinc-500">
                Le scope détermine où ce fait sera accessible
              </p>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Catégorie <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {categories.map(cat => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.slug }))}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    formData.category === cat.slug
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-zinc-300 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                      {cat.name}
                    </span>
                  </div>
                  {cat.description && (
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 line-clamp-2">
                      {cat.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Fact Text */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Fait <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.fact_text}
              onChange={(e) => setFormData(prev => ({ ...prev, fact_text: e.target.value }))}
              placeholder="Ex: Le client préfère être contacté par email le matin"
              required
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />
            <p className="mt-1 text-xs text-zinc-500">
              Décrivez le fait de manière claire et concise
            </p>
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Résumé (optionnel)
            </label>
            <input
              type="text"
              value={formData.fact_summary}
              onChange={(e) => setFormData(prev => ({ ...prev, fact_summary: e.target.value }))}
              placeholder="Résumé court du fait"
              maxLength={500}
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Subcategory */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Sous-catégorie (optionnel)
            </label>
            <input
              type="text"
              value={formData.subcategory}
              onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
              placeholder="Ex: Communication, Horaires, Produit X"
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Confidence Level */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Niveau de confiance
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, confidence_level: 'verified' }))}
                className={`p-3 rounded-xl border-2 transition-all ${
                  formData.confidence_level === 'verified'
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-zinc-300 dark:border-zinc-700'
                }`}
              >
                <div className="font-medium text-sm">✓ Vérifié</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, confidence_level: 'auto' }))}
                className={`p-3 rounded-xl border-2 transition-all ${
                  formData.confidence_level === 'auto'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-zinc-300 dark:border-zinc-700'
                }`}
              >
                <div className="font-medium text-sm">Auto</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, confidence_level: 'deprecated' }))}
                className={`p-3 rounded-xl border-2 transition-all ${
                  formData.confidence_level === 'deprecated'
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                    : 'border-zinc-300 dark:border-zinc-700'
                }`}
              >
                <div className="font-medium text-sm">Déprécié</div>
              </button>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Ajouter un tag..."
                className="flex-1 px-4 py-2 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 rounded-xl bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 transition-colors"
              >
                <Tag className="w-5 h-5" />
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:text-emerald-900 dark:hover:text-emerald-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Valid Until */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Valide jusqu'au (optionnel)
            </label>
            <input
              type="date"
              value={formData.valid_until}
              onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-zinc-500">
              Laisser vide pour un fait permanent
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Priorité: {formData.priority}
            </label>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: Number(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-zinc-500">
              <span>Basse (0)</span>
              <span>Haute (10)</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-full border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !formData.fact_text || !formData.category || (!fact && !formData.memory_scope_id)}
              className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {fact ? 'Mettre à jour' : 'Créer le fait'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
